import * as React from 'react';
import { Dialog, DialogTitle, DialogContent, Button, DialogActions, Table, TableHead, TableRow, TableBody, TableCell, TablePagination, Checkbox, TableSortLabel, Paper, TableContainer, TextField, InputAdornment, Icon } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { IAccessRelationData, IRolePerrmission } from './AccessDomainModels';
import useDebounce from '../../utils/useDebounce';
import LoadingIcon from '../../components/LoadingIcon';
import lang from "../Relations/RelationsLang";
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ALERT } from '../../services/ResourceRelationsService';
import Moment from "moment";
import { IUserAccessDataRow } from './AccessUserAccessesService';
import clsx from "clsx";
import { OptionType } from '../EmployeesComboboxMI';

import "./UserAccessesWindow.css";
import { SensitiveDataWarningDialog } from './RolePermissionControlCommon';
import LogCompanyTooltip from '../LogCompanyTooltip';

function desc<T>(a: T, b: T, orderBy: keyof T)
{
	let aValue = columnToString(orderBy as string, a as any) ?? "";
	let bValue = columnToString(orderBy as string, b as any) ?? "";

	return aValue.localeCompare(bValue);
}

function stableSort<T>(array: T[], cmp: (a: T, b: T) => number) {
	const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
	stabilizedThis.sort((a, b) => {
		const order = cmp(a[0], b[0]);
		if (order !== 0) return order;
		return a[1] - b[1];
	});
	return stabilizedThis.map(el => el[0]);
}

type Order = 'asc' | 'desc';

function getSorting<K extends keyof any>(
	order: Order,
	orderBy: K,
): (a: { [key in K]: number | string | boolean | IRolePerrmission }, b: { [key in K]: number | string }) => number {

	return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

interface EnhancedTableProps {
	classes: ReturnType<typeof useStyles>;
	numSelected: number;
	onRequestSort: (event: React.MouseEvent<unknown>, property: keyof IUserAccessDataRow) => void;
	onSelectAllClick: (event: React.MouseEvent<HTMLInputElement>) => void;
	order: Order;
	orderBy: string;
	rowCount: number;

	canSelect: boolean,
	config: IUserAccessesWindowConfig
}

export interface IUserAccessesWindowConfig {
	filters: string[],
	columns: HeadRow[];
}

interface HeadRow {
	disablePadding: boolean;
	id: keyof IUserAccessDataRow;
	labelPl: string;
	labelEn?: string;
	labelDe?: string;
	labelEs?: string;
	className?: string;
	minWidth?: number;
	searched: boolean;
}

const defaultConfig: IUserAccessesWindowConfig = {
	filters: [],
	columns: [
		{ id: 'system', disablePadding: true, labelPl: 'System', minWidth: 230, searched: true },
		{ id: 'period', disablePadding: false, labelPl: lang.period(), minWidth: 170, searched: false },
		{ id: 'role', disablePadding: false, labelPl: 'Rola uprawnień', searched: true },
		{ id: 'relationStatus', disablePadding: false, labelPl: 'Status', searched: false },
		{ id: 'followFromPersonProfile', disablePadding: false, labelPl: '', searched: false },
]};

function EnhancedTableHead(props: EnhancedTableProps) {
	const { classes, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
	const createSortHandler = (property: keyof IUserAccessDataRow) => (event: React.MouseEvent<unknown>) => {
		onRequestSort(event, property);
	};

	return (
		<TableHead>
			<TableRow>
				{props.canSelect && <TableCell padding="checkbox">
					<Checkbox
						color="primary"
						indeterminate={numSelected > 0 && numSelected < rowCount}
						checked={numSelected === rowCount}
						onClick={onSelectAllClick}
						inputProps={{ 'aria-label': 'select all desserts' }}
					/>
				</TableCell>}
				{props.config.columns.map(column => (
					<TableCell
						key={column.id}
						padding={column.disablePadding ? 'none' : 'default'}
						sortDirection={orderBy === column.id ? order : false}
						className={clsx("table-header", classes.headerCell, column.className)}
						style={{ minWidth: column.minWidth }}
					>
						<TableSortLabel
							active={orderBy === column.id}
							direction={order}
							onClick={createSortHandler(column.id)}
						>
							{lang.select(column.labelPl, column.labelEn, column.labelDe, column.labelEs)}
							{orderBy === column.id ? (
								<span className={classes.visuallyHidden}>
									{order === 'desc' ? 'sorted descending' : 'sorted ascending'}
								</span>
							) : null}
						</TableSortLabel>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			width: '100%',
			marginTop: theme.spacing(3),
		},
		container: {
			maxHeight: 'calc(100vh - 300px)',
		},
		paper: {
			width: '100%',
			marginBottom: theme.spacing(2),
		},
		table: {
		},
		headerCell: {
			whiteSpace: 'nowrap',
		},
		tableCell: {
			whiteSpace: 'nowrap',
		},
		visuallyHidden: {
			border: 0,
			clip: 'rect(0 0 0 0)',
			height: 1,
			margin: -1,
			overflow: 'hidden',
			padding: 0,
			position: 'absolute',
			top: 20,
			width: 1,
		},
	}), {
		name: "UserAccessesWindow",
	}
);

interface ITableCellAccessProps {
	row: IUserAccessDataRow
	column: HeadRow
}

function parseDate(isoDate: string): string {
	const qdesk = (window as any).qdesk;
	let date: string = null;
	if (isoDate)
		date = qdesk.moment.format(isoDate, qdesk.settings.dateFormat);
	return date;
}

function TableCellAccess(props: ITableCellAccessProps) {
	const { column, row } = props;
	
	switch (column.id) {
		case "system":
			return <>{row.applicationName} {row.applicationName && <> &mdash; </>} {row.name}</>
		case "period":
			return <>{parseDate(row.inceptionDate)} &mdash; {parseDate(row.expirationDate)}</>
		case "inceptionDate":
			return <>{parseDate(row.inceptionDate)}</>
		case "expirationDate":
			return <>{parseDate(row.expirationDate)}</>

		case "role":
			return <>{row.role && <span>{row.role.name}</span>}</>
		case "sensitiveData":
			return <>{row.role && <span>{row.role.sensitiveData && <Icon>done</Icon>}</span>}</>
		case "reasonForProcessingSensitiveData":
			return <>{row.role && <span>{row.role.reasonForProcessingSensitiveData}</span>}</>

		case "passwordExist":
			return <>{row.passwordExist && <span>*******</span>}</>

		case "company":
			return <LogCompanyTooltip value={row.company} />

		case "followFromPersonProfile":
			return <>{row.followFromPersonProfile && <i className="material-icons text-gray" title={lang.get("resultsFromWorkPosition")} style={{ color: "#888" }}>person</i>}</>
		default:
			return <>{row[column.id]}</>
	};
}

function columnToString(columnId: string, row: IUserAccessDataRow): string {

	switch (columnId) {
		case "system":
			return (row.applicationName ?? "") + "|" + (row.name ?? "");
		case "period":
			return parseDate(row.inceptionDate) + "|" + parseDate(row.expirationDate);
		case "inceptionDate":
			return parseDate(row.inceptionDate);
		case "expirationDate":
			return parseDate(row.expirationDate);
		case "role":
			return row.role?.name;
		case "passwordExist":
			return null;
		case "followFromPersonProfile":
			return null;
	};

	if (typeof row[columnId] === 'string')
		return row[columnId];

	return null;
}

function search(searchText: string, searchedColumns: string[], data: IUserAccessDataRow): boolean {
	if (!searchText) {
		return true;
	}

	for (let i = 0; i < searchedColumns.length; i++){
		let searchedColumn = searchedColumns[i];
		let columnText = columnToString(searchedColumn, data);
		
		if (!columnText) {
			continue;
		}

		if (columnText.toLocaleLowerCase().indexOf(searchText) >= 0)
			return true;
	}
	
	return false;
}

interface IEnhancedTableProps {
	canInsert: boolean,
	rowsPerUsers: IUserAccessDataRow[],
	blockedRelationIds: () => number[],
	config: IUserAccessesWindowConfig,

	selected: number[]
	onSelectedChange: (selected: number[]) => void;
	page: number;
	onPageChange: (page: number) => void;
	search: string;
}


function EnhancedTable(props: IEnhancedTableProps) {
	const classes = useStyles({});

	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<keyof IUserAccessDataRow>('system');
	const [rowsPerPage, setRowsPerPage] = React.useState(10);

	const [showWarningModal, setShowWarningModal] = React.useState<IUserAccessDataRow>(null);

	const [config, setConfig] = React.useState<IUserAccessesWindowConfig>(defaultConfig);
	const [rows, setRows] = React.useState<IUserAccessDataRow[]>(props.rowsPerUsers);

	React.useEffect(() => {

		if (!props.search) {
			setRows(props.rowsPerUsers);
			return;
		}

		var searchedColumns = config.columns.filter(x => x.searched).map(x => x.id);
		let searchText = props.search.toLowerCase().trim();
		setRows(props.rowsPerUsers.filter(x => search(searchText, searchedColumns, x)));

	}, [config, props.rowsPerUsers, props.search]);

	React.useEffect(() => {
		setConfig({ ...defaultConfig, ...props.config });
	}, [props.config])

	function handleChangePage(event: unknown, newPage: number) {
		props.onPageChange(newPage);
	}

	function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
		setRowsPerPage(+event.target.value);
		props.onPageChange(0);
	}

	function handleRequestSort(event: React.MouseEvent<unknown>, property: keyof IUserAccessDataRow) {
		const isDesc = orderBy === property && order === 'desc';
		setOrder(isDesc ? 'asc' : 'desc');
		setOrderBy(property);
	}

	function handleSelectAllClick(event: React.MouseEvent<HTMLInputElement>) {
		var checked = props.selected.length === rows.length;
		let indeterminate = (event.target as any).indeterminate;
		if (indeterminate || !checked) {
			const newSelecteds = rows.filter(x => !x.followFromPersonProfile).map(n => n.relationId);
			props.onSelectedChange(newSelecteds);
			return;
		}
		props.onSelectedChange([]);
	}

	function handleClick(event: React.MouseEvent<unknown>, row: IUserAccessDataRow) {

		if (row.followFromPersonProfile) {
			return;
		}

		if (row.role?.sensitiveData && !isSelected(row.relationId))
		{
			setShowWarningModal(row);
			return;
		}

		selectRow(row);
	}

	function selectRow(row: IUserAccessDataRow) {
		const selectedIndex = props.selected.indexOf(row.relationId);
		let newSelected: number[] = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(props.selected, row.relationId);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(props.selected.slice(1));
		} else if (selectedIndex === props.selected.length - 1) {
			newSelected = newSelected.concat(props.selected.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				props.selected.slice(0, selectedIndex),
				props.selected.slice(selectedIndex + 1),
			);
		}

		props.onSelectedChange(newSelected);
	}

	let blockedRelationIds = props.blockedRelationIds();
	const isBlocked = (realtionId: number) => blockedRelationIds.indexOf(realtionId) !== -1;
	const isSelected = (realtionId: number) => props.selected.indexOf(realtionId) !== -1;

	return <Paper className={classes.root}>
		<TableContainer className={classes.container} >
			<Table
				stickyHeader
				className={classes.table}
			>
				<EnhancedTableHead
					classes={classes}
					numSelected={props.selected.length}
					order={order}
					orderBy={orderBy}
					onSelectAllClick={handleSelectAllClick}
					onRequestSort={handleRequestSort}
					rowCount={rows.length}
					canSelect={props.canInsert}
					config={config}
				/>
				<TableBody>
					{stableSort(rows, getSorting(order, orderBy)).slice(props.page * rowsPerPage, props.page * rowsPerPage + rowsPerPage)
						.map((row: IUserAccessDataRow, index) => {

							var isItemBlocked = isBlocked(row.relationId);
							var isItemSelected = true;
							if (!isItemBlocked) {
								isItemSelected = isSelected(row.relationId);
							}

							return (<TableRow
								hover
								onClick={event => !isItemBlocked && handleClick(event, row)}
								role="checkbox"
								tabIndex={-1}
								key={row.relationId}
								selected={isItemSelected}
								className={clsx({
									"row-selected": isItemSelected
								})}
							>
								{props.canInsert && <TableCell padding="checkbox">
									<Checkbox
										className="mi-checkbox-input"
										disabled={row.followFromPersonProfile || isItemBlocked}
										color="primary"
										checked={isItemSelected}
									/>
								</TableCell>}
								{config.columns.map(column => (
									<TableCell
										key={column.id}
										padding={column.disablePadding ? 'none' : 'default'}
										className={clsx(classes.tableCell, "table-row", column.className)}
									>
										<TableCellAccess column={column} row={row} />
									</TableCell>
								))}
							</TableRow>)
						})}
				</TableBody>
			</Table>
		</TableContainer>
		<TablePagination
			rowsPerPageOptions={[10, 25, 50, 100]}
			component="div"
			count={rows.length}
			rowsPerPage={rowsPerPage}
			page={props.page}
			backIconButtonProps={{
				'aria-label': 'previous page',
			}}
			nextIconButtonProps={{
				'aria-label': 'next page',
			}}
			onChangePage={handleChangePage}
			onChangeRowsPerPage={handleChangeRowsPerPage}
			labelRowsPerPage="Wierszy na stronie:"
			labelDisplayedRows={(paginagionInfo) => {
				return <span>{paginagionInfo.from}
					{"-"}
					{paginagionInfo.to}
					{" z "} {paginagionInfo.count}
				</span>
			}}
		/>
		<SensitiveDataWarningDialog
			showWarningModal={showWarningModal?.role ? [showWarningModal.role] : []}
			onClose={() => setShowWarningModal(null)}
			onAccept={() => {
				selectRow(showWarningModal);
				setShowWarningModal(null);
			}}
		/>
	</Paper>
}

interface IUserAccessesWindowProps {
	user: OptionType,
	canInsert: boolean,
	onInsert: (items: IAccessRelationData[]) => void,
	rowsPerUsers: IUserAccessDataRow[],
	loading: boolean,
	blockedRelationIds: () => number[],

	config: IUserAccessesWindowConfig,
}

export default function UserAccessesWindow(props: IUserAccessesWindowProps) {
	const [open, setOpen] = React.useState(false);

	const [selected, setSelected] = React.useState<number[]>([]);
	const [page, setPage] = React.useState(0);
	
	const [search, setSearch] = React.useState("");
	const [searchLazy, setSearchLazy] = React.useState("");
	const debouncedSearchTerm = useDebounce(search, 1500);

	React.useEffect(() => {
		setPage(0);
		setSearchLazy(search);
	}, [debouncedSearchTerm]);

	function handleClickOpen() {
		setOpen(true);
		setPage(0);
	}

	function handleClose() {
		setOpen(false);
	}

	const isSelected = (realtionId: number) => selected.indexOf(realtionId) !== -1;

	if (!props.user?.value)
		return null;

	const rows: IUserAccessDataRow[] = props.rowsPerUsers;
	let content = null;
	if (props.loading)
		content = <LoadingIcon />;
	else if (rows) {
		content = <EnhancedTable
			blockedRelationIds={props.blockedRelationIds}
			canInsert={props.canInsert}
			config={props.config}
			onPageChange={setPage}
			onSelectedChange={setSelected}
			page={page}
			selected={selected}
			rowsPerUsers={props.rowsPerUsers}
			search={searchLazy}
		/>
	}

	function handleAddSelected() {
		setOpen(false);

		let items: IAccessRelationData[] = [];
		for (let i = 0; i < rows.length; i++) {
			let row = rows[i] as IUserAccessDataRow;
			if (isSelected(row.relationId)) {

				let from = null;
				let to = null;

				if (row.inceptionDate)
					from = Moment(row.inceptionDate)
				if (row.expirationDate)
					to = Moment(row.expirationDate);

				let item: IAccessRelationData = {
					alert: ALERT.Access,
					employee: {
						id: parseInt(props.user.value),
						name: props.user.label,
						kind: 'Employee',
						label: props.user.label,
						type: null,
					},
					from: from,
					to: to,
					beforeTo: to,
					relationId: row.relationId,

					login: row.login || "",
					environment: row.environment || "",
					password: "",
					passwordExist: row.passwordExist || null,
					personAccountDescription: row.personAccountDescription || "",
					personAccountType: row.personAccountType || "",
					company: row.company || "",
					referenceNumber: row.referenceNumber || "",
					systemName: row.systemName || "",
					telephone: row.telephone || "",
					email: row.email || "",
					location: row.location || "",

					description: "",
					resource: {
						id: row.resourceId,
						label: (row.applicationName ? row.applicationName + " - " + row.name : row.name),
						kind: row.kind,
					},
					role: row.role,
					deleted: false,
					id: null,
					key: null,
					excluded: [],
					errorMessages: [],
				}

				items.push(item);
			}
		}
		props.onInsert(items);
	}

	return (
		<div className="user-accesses-window">
			<button type="button" className="qd-btn qd-btn-default" onClick={handleClickOpen}>
				{lang.get("currentAccesses")}
			</button>
			<Dialog
				fullWidth
				maxWidth="lg"
				open={open}
				onClose={handleClose}
				className="user-accesses-window-dialog"
			>
				<DialogTitle>
					<TextField
						variant="outlined"
						placeholder={lang.get("search") + "..."}

						value={search}
						onChange={(e) => setSearch(e.target.value)}
						size="small"
						style={{
							float: "right"
						}}
						InputProps={{
							startAdornment: <InputAdornment position="start" > <SearchIcon color="disabled" /></InputAdornment>
						}}
					/>
					{lang.get("accessesFor")} {props.user.label}
				</DialogTitle>
				<DialogContent>
					{content}
				</DialogContent>
				<DialogActions>
					{props.canInsert && <Button type="button" onClick={handleAddSelected} color="primary" disabled={selected.length == 0}>
						{lang.get("addToIssue")}
					 </Button>}
					<Button type="button" onClick={handleClose} autoFocus>
						{lang.get("close")}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}